import React, { useState, useEffect } from "react";
import Nav from "./Nav";
import { useNavContext } from "../context/NavContext";
import Swal from "sweetalert2";
import {
  collection,
  getFirestore,
  onSnapshot,
  doc,
  deleteDoc,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import GrupoModal from "./GrupoModal";
import Spinner from "./Spinner";
import { useNavigate } from "react-router-dom";
import Paginador from "./Paginador";
import { useLocation } from "react-router-dom";

const Grupos = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const [selectedGrupos, setSelectedGrupos] = useState([]);
  const [grupoEditando, setGrupoEditando] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(25); // Valor inicial
  const [currentPage, setCurrentPage] = useState(1);

  // Limpia el buscador al cambiar de ruta
  useEffect(() => {
    setSearchTerm("");
  }, [location.pathname, setSearchTerm]);

  useEffect(() => {
    const fetchGrupos = () => {
      setLoading(true); // Establecer loading a true antes de comenzar la carga

      const firestore = getFirestore();
      const gruposRef = collection(firestore, "grupos");

      const unsubscribe = onSnapshot(gruposRef, (snapshot) => {
        const fetchedGrupos = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setGrupos(fetchedGrupos); // Actualizar el estado local con las categorías
        setLoading(false); // Indicar que los datos ya están listos
      });

      return unsubscribe;
    };

    const unsubscribe = fetchGrupos();
    return () => unsubscribe(); // Cleanup de la suscripción
  }, []);

  const handleSubmitGrupo = async (newGrupo) => {
    if (newGrupo.id) {
      // **Editar una categoría existente**

      // Actualizar el estado local de manera optimista
      setGrupos((prevGrupos) =>
        prevGrupos.map((cat) =>
          cat.id === newGrupo.id
            ? {
                ...cat,
                nombre: newGrupo.nombre,
                descripcion: newGrupo.descripcion,
              }
            : cat
        )
      );

      // Cerrar el modal inmediatamente
      handleCloseModal();

      try {
        const firestore = getFirestore();
        const grupoDocRef = doc(firestore, "grupos", newGrupo.id);
        let imageUrl = newGrupo.imagenUrl || "";

        // Subir la imagen si se ha seleccionado una nueva
        if (newGrupo.imagen && typeof newGrupo.imagen !== "string") {
          const storage = getStorage();
          const imageRef = ref(storage, `grupos/${newGrupo.imagen.name}`);
          const snapshot = await uploadBytes(imageRef, newGrupo.imagen);
          imageUrl = await getDownloadURL(snapshot.ref);
        }

        // Actualizar la categoría en Firestore
        await updateDoc(grupoDocRef, {
          nombre: newGrupo.nombre,
          descripcion: newGrupo.descripcion || "",
          imagenUrl: imageUrl || "",
        });

        // Actualizar el estado local con la URL de la imagen
        setGrupos((prevGrupos) =>
          prevGrupos.map((cat) =>
            cat.id === newGrupo.id ? { ...cat, imagenUrl: imageUrl } : cat
          )
        );

        Swal.fire({
          title: "Grupo actualizado!",
          text: "Los cambios se guardaron correctamente.",
          icon: "success",
          confirmButtonText: "Ok",
        });
      } catch (error) {
        console.error("Error al actualizar el grupo:", error);
        Swal.fire({
          title: "Error",
          text: "Hubo un problema al actualizar el grupo. Inténtalo de nuevo.",
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    } else {
      // Asignar un ID temporal
      const tempId = `temp-${Date.now()}`;
      const tempGrupo = {
        id: tempId,
        nombre: newGrupo.nombre,
        descripcion: newGrupo.descripcion || "",
        imagenUrl: "", // Puedes asignar una URL de imagen temporal si lo deseas
      };

      // Actualizar el estado local de manera optimista
      setGrupos((prevGrupos) => [...prevGrupos, tempGrupo]);

      // Cerrar el modal inmediatamente
      handleCloseModal();

      try {
        const firestore = getFirestore();
        const grupoRef = collection(firestore, "grupos");
        let imageUrl = "";

        // Subir la imagen si se ha seleccionado una nueva
        if (newGrupo.imagen && typeof newGrupo.imagen !== "string") {
          const storage = getStorage();
          const imageRef = ref(storage, `grupos/${newGrupo.imagen.name}`);
          const snapshot = await uploadBytes(imageRef, newGrupo.imagen);
          imageUrl = await getDownloadURL(snapshot.ref);
        }

        // Agregar la nueva categoría a Firestore
        const docRef = await addDoc(grupoRef, {
          nombre: newGrupo.nombre,
          descripcion: newGrupo.descripcion || "",
          imagenUrl: imageUrl || "",
        });

        // Reemplazar la categoría temporal con la real en el estado local
        setGrupos((prevGrupos) =>
          prevGrupos.map((cat) =>
            cat.id === tempId
              ? { ...cat, id: docRef.id, imagenUrl: imageUrl }
              : cat
          )
        );

        Swal.fire({
          title: "Grupo agregado!",
          text: "El grupo se agregó correctamente.",
          icon: "success",
          confirmButtonText: "Ok",
        });
      } catch (error) {
        console.error("Error al agregar el grupo:", error);
        Swal.fire({
          title: "Error",
          text: "Hubo un problema al agregar el grupo. Inténtalo de nuevo.",
          icon: "error",
          confirmButtonText: "Ok",
        });

        // Remover la categoría temporal del estado local si ocurre un error
        setGrupos((prevGrupos) =>
          prevGrupos.filter((cat) => cat.id !== tempId)
        );
      }
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      // Si se marca el checkbox de "seleccionar todos", selecciona todas las cotizaciones
      const allGruposIds = filteredGrupos.map((grupo) => grupo.id);
      setSelectedGrupos(allGruposIds);
    } else {
      // Si se desmarca, vacía la lista de seleccionados
      setSelectedGrupos([]);
    }
  };

  const filteredGrupos = React.useMemo(() => {
    if (!grupos || grupos.length === 0) return [];
    const searchLower = searchTerm.toLowerCase();
    return grupos.filter((grupo) => {
      const searchableFields = [
        grupo.nombre?.toLowerCase() || "",
        grupo.descripcion?.toLowerCase() || "",
      ];
      return searchableFields.some((field) => field.includes(searchLower));
    });
  }, [grupos, searchTerm]);

  const handleSelectGrupo = (grupoId) => {
    setSelectedGrupos((prevSelected) => {
      if (prevSelected.includes(grupoId)) {
        return prevSelected.filter((id) => id !== grupoId);
      } else {
        return [...prevSelected, grupoId];
      }
    });
  };

  const handleOpenModal = () => {
    setGrupoEditando(null); // Asegúrate de que no hay categoría editando
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const handleEditarGrupo = (grupo) => {
    setGrupoEditando(grupo); // Establece la categoría que se está editando
    setIsModalOpen(true); // Abre el modal
  };

  const handleVerDetalles = (grupoNombre) => {
    navigate(`/grupos/${grupoNombre}/productos`); // Navegar con el nombre de la categoría
  };

  const handleDeleteSelected = async () => {
    try {
      const confirmed = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará los grupos seleccionadas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });

      if (confirmed.isConfirmed) {
        const firestore = getFirestore();
        const gruposEliminados = [];
        const gruposRef = collection(firestore, "grupos");

        for (const grupoId of selectedGrupos) {
          const grupoDocRef = doc(gruposRef, grupoId);
          await deleteDoc(grupoDocRef);
          gruposEliminados.push(grupoId);
        }

        const gruposRestantes = grupos.filter(
          (grupo) => !gruposEliminados.includes(grupo.id)
        );
        setGrupos(gruposRestantes);
        setSelectedGrupos([]);

        // Mostrar alerta de éxito
        await Swal.fire({
          title: "Categoría eliminada!",
          text: "Las categorías seleccionadas han sido eliminadas correctamente.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Ok",
          scrollbarPadding: false,
        });
      }
    } catch (error) {
      console.error("Error al eliminar categoría:", error);
      Swal.fire({
        title: "Error",
        text: "Hubo un problema al eliminar las categorías. Inténtalo de nuevo.",
        icon: "error",
        confirmButtonText: "Ok",
        scrollbarPadding: false,
      });
    }
  };

  const totalGrupos = filteredGrupos.length;

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Nav
        handleSearch={setSearchTerm}
        proximasAVencer={proximasAVencer}
        proximosEventos={proximosEventos}
      />

      <div className="task-filters">
        <div className="header-actions-proyectos">
          <button className="action-button-proyectos">
            <i className="bi bi-funnel-fill"></i> Todos
            <i className="bi bi-chevron-down" style={{ fontSize: "12px" }}></i>
          </button>
          <div className="buscador-pedidos">
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="buscador-input"
              placeholder="Buscar grupos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} // Actualizar el término de búsqueda
            />
          </div>
        </div>
        <div className="task-filters-right">
          <div
            className="view-buttons-container"
            style={{ position: "relative" }}
          >
            {/* Botón Eliminar */}
            <button
              type="button"
              className="delete-button"
              style={{
                visibility: selectedGrupos.length > 0 ? "visible" : "hidden",
                opacity: selectedGrupos.length > 0 ? 1 : 0,
                transform:
                  selectedGrupos.length > 0 ? "scale(1)" : "scale(0.9)",
                transition:
                  "visibility 0s linear 0.2s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
              }}
              onClick={handleDeleteSelected}
            >
              Eliminar
            </button>

            {/* Menú de opciones */}
            <button className="view-button">
              <i className="bi bi-three-dots-vertical"></i>
            </button>

            {/* Exportar */}
            <button className="exportar-button">
              <i className="bi bi-cloud-arrow-down"></i> Exportar
            </button>

            {/* + Nuevo Grupo */}
            <button className="new-task-button" onClick={handleOpenModal}>
              + Nuevo Grupo
            </button>
          </div>
        </div>
      </div>

      <GrupoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitGrupo}
        grupo={grupoEditando}
      />

      <div className="cotizaciones-table">
        <div className="tabla-cotizaciones-wrapper">
          <div className="tabla-cotizaciones-content">
            {loading ? ( // Mostrar spinner mientras los datos se cargan
              <Spinner />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={handleSelectAll}
                        className="checkbox-select-all check"
                      />
                    </th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGrupos.map((grupo) => (
                    <tr key={grupo.id}>
                      <td className="check">
                        <input
                          type="checkbox"
                          checked={selectedGrupos.includes(grupo.id)}
                          onChange={() => handleSelectGrupo(grupo.id)}
                          className="checkbox"
                        />
                      </td>
                      <td>{grupo.nombre}</td>
                      <td>{grupo.descripcion}</td>
                      <td className="acciones-container">
                        <div className="acciones-icons">
                          <div
                            className="icon-wrapper"
                            onClick={() => handleVerDetalles(grupo.nombre)}
                          >
                            <i className="bi bi-eye"></i>
                            <span className="tooltipCategorias">
                              Mostrar Detalles
                            </span>
                          </div>
                          <div
                            className="icon-wrapper"
                            onClick={() => handleEditarGrupo(grupo)}
                          >
                            <i className="bi bi-pencil"></i>
                            <span className="tooltipCategorias">Editar</span>
                          </div>
                          <div className="icon-wrapper">
                            <i className="bi bi-shield-slash"></i>
                            <span className="tooltipCategorias">
                              Desactivar
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="tabla-cotizaciones-footer">
            <div className="paginador-container">
              <Paginador
                totalItems={totalGrupos}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Grupos;
